import { motion } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import { AnimatedCounter, Reveal, SectionHeader } from "@/components/shared";
import { TECH_GRID, REGULATORY, PERFORMANCE, FEATURED_ASSETS, TEAM_IMAGE, PARTNERS } from "@/data";

export function TechMarquee() {
  const items = [...PARTNERS, ...PARTNERS];
  return (
    <section className="py-16 border-y border-white/10 overflow-hidden bg-auvra-surface" data-testid="partners-marquee">
      <div className="container-auvra mb-8">
        <span className="kicker">Trusted by global institutions</span>
      </div>
      <div className="relative flex overflow-hidden">
        <div className="flex animate-marquee whitespace-nowrap gap-16 pr-16">
          {items.map((p, i) => (
            <span key={i} className="text-2xl md:text-3xl font-semibold text-white/25 hover:text-white/60 transition-colors">
              {p}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

export function InstitutionalTech() {
  return (
    <section className="container-auvra py-24 md:py-32 lg:py-40" data-testid="institutional-tech">
      <SectionHeader
        chapter="04"
        kicker="Institutional Technology"
        title="A complete tokenization stack"
        subtitle="Every layer of the asset lifecycle — from identity to secondary markets — engineered as composable institutional infrastructure."
      />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        {TECH_GRID.map((t, i) => (
          <Reveal key={t.title} i={i % 4}>
            <motion.div
              whileHover={{ y: -4 }}
              className="glass rounded-2xl p-6 md:p-8 h-full hover:border-auvra-blue/40 transition-colors group"
              data-testid={`tech-card-${i}`}
            >
              <t.icon className="w-7 h-7 text-auvra-blue mb-8 group-hover:scale-110 transition-transform" />
              <h4 className="text-base md:text-lg font-medium">{t.title}</h4>
            </motion.div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

export function Regulatory() {
  return (
    <section className="container-auvra py-24 md:py-32 lg:py-40" data-testid="regulatory">
      <SectionHeader
        chapter="05"
        kicker="Regulatory Infrastructure"
        title="Compliance embedded at every layer"
        subtitle="AUVRA enforces regulatory requirements programmatically — so every transaction is compliant by construction."
      />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {REGULATORY.map((r, i) => (
          <Reveal key={r.title} i={i % 3}>
            <div className="border border-white/10 rounded-2xl p-8 h-full hover:bg-white/[0.02] transition-colors" data-testid={`reg-card-${i}`}>
              <div className="flex items-center gap-3 mb-4">
                <r.icon className="w-5 h-5 text-auvra-blue" />
                <h4 className="text-lg font-medium">{r.title}</h4>
              </div>
              <p className="text-secondary-auvra leading-relaxed">{r.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

export function Performance() {
  return (
    <section className="bg-auvra-surface border-y border-white/10 py-24 md:py-32" data-testid="performance">
      <div className="container-auvra">
        <SectionHeader
          chapter="06"
          kicker="Performance Metrics"
          title="Proven at institutional scale"
          center
        />
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {PERFORMANCE.map((p, i) => (
            <Reveal key={p.label} i={i % 3}>
              <div className="glass rounded-2xl p-8 text-center" data-testid={`perf-card-${i}`}>
                <p.icon className="w-6 h-6 text-auvra-blue mx-auto mb-5" />
                <div className="text-3xl md:text-4xl font-semibold tracking-tight">
                  <AnimatedCounter value={p.value} suffix={p.suffix} />
                </div>
                <div className="text-sm text-secondary-auvra mt-2">{p.label}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function FeaturedAssets({ onInvest }) {
  return (
    <section className="container-auvra py-24 md:py-32 lg:py-40" data-testid="featured-assets">
      <SectionHeader
        chapter="07"
        kicker="Featured Assets"
        title="Tokenized opportunities, live now"
        subtitle="Institutional-grade real estate, fractionalized and available to eligible investors."
      />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {FEATURED_ASSETS.map((a, i) => (
          <Reveal key={a.name} i={i}>
            <motion.div
              whileHover={{ y: -8 }}
              transition={{ duration: 0.4, ease: [0.76, 0, 0.24, 1] }}
              className="rounded-2xl overflow-hidden border border-white/10 bg-auvra-elevated group"
              data-testid={`asset-card-${i}`}
            >
              <div className="relative h-56 overflow-hidden">
                <img src={a.image} alt={a.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
                <span className="absolute top-4 right-4 text-xs font-medium px-3 py-1 rounded-full bg-auvra-blue/90 text-white">
                  {a.status}
                </span>
                <div className="absolute bottom-4 left-4 text-xs text-white/80">{a.location}</div>
              </div>
              <div className="p-6">
                <h4 className="text-lg font-medium mb-4">{a.name}</h4>
                <div className="flex justify-between border-t border-white/10 pt-4 mb-6">
                  <div>
                    <div className="text-xs text-secondary-auvra">Asset value</div>
                    <div className="font-semibold">{a.value}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-secondary-auvra">Expected yield</div>
                    <div className="font-semibold text-auvra-blue">{a.yield}</div>
                  </div>
                </div>
                <button
                  onClick={onInvest}
                  data-testid={`asset-invest-btn-${i}`}
                  className="w-full flex items-center justify-center gap-2 border border-white/15 hover:border-auvra-blue hover:bg-auvra-blue/10 rounded-full py-3 text-sm font-medium transition-all"
                >
                  Invest <ArrowUpRight className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

export function ResponsibleInvesting() {
  const points = ["Governance", "Transparency", "Compliance", "Security", "Long-term investing"];
  return (
    <section className="container-auvra py-24 md:py-32 lg:py-40" data-testid="responsible-investing">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <Reveal>
          <div className="relative rounded-2xl overflow-hidden">
            <img src={TEAM_IMAGE} alt="AUVRA institutional team" className="w-full h-[420px] object-cover grayscale hover:grayscale-0 transition-all duration-700" />
            <div className="absolute inset-0 ring-1 ring-inset ring-white/10 rounded-2xl" />
          </div>
        </Reveal>
        <div>
          <span className="font-display italic text-auvra-blue text-2xl md:text-3xl">08</span>
          <h2 className="text-4xl md:text-5xl font-semibold tracking-tight leading-[1.05] mt-4 mb-6">
            Responsible institutional investing
          </h2>
          <p className="text-secondary-auvra leading-relaxed mb-8">
            We build for the long term. Every AUVRA offering is grounded in rigorous governance, radical
            transparency and uncompromising security — the foundations institutional capital requires.
          </p>
          <div className="space-y-4">
            {points.map((p, i) => (
              <Reveal key={p} i={i} className="flex items-center gap-3">
                <span className="w-6 h-6 rounded-full bg-auvra-blue/15 flex items-center justify-center">
                  <Check className="w-3.5 h-3.5 text-auvra-blue" />
                </span>
                <span className="text-white/90">{p}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
